import { useEffect, useMemo, useRef, useState } from 'react';
import { useClicker } from '@hook/common/useEvent';

interface ITiming {
  begin?: number;
  gap?: number;
  end?: number;
}

interface IUseAudioPlayerParams {
  sources: string[];
  timing?: ITiming;
}

export interface IUseAudioPlayerResult {
  isPlaying: boolean;
  isReplayable: boolean;
  audioRefs: React.MutableRefObject<HTMLAudioElement[]>;
  currentAudioIdx: number;
  handlePlayCompleted: () => Promise<void>;
  handlePause: () => void;
  handlePlayAndPauseClick: ((...args: any[]) => void) | undefined;
  handleReplayClick: ((...args: any[]) => void) | undefined;
}

const sleep = (time: number) => new Promise((resolve) => setTimeout(resolve, time));

export const useAudioPlayer = ({ sources, timing = {} }: IUseAudioPlayerParams): IUseAudioPlayerResult => {
  const sourcesKey = sources?.join('|') ?? '';
  const stableSources = useMemo(() => sources, [sourcesKey]);
  const timingBegin = timing.begin ?? 0;
  const timingGap = timing.gap ?? 0;
  const timingEnd = timing.end ?? 0;

  const audioRefs = useRef<HTMLAudioElement[]>([]);
  const [currentAudioIdx, setCurrentAudioIdx] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isInitial, setIsInitial] = useState<boolean>(true);
  const [isReplayable, setIsReplayable] = useState<boolean>(false);
  const playSessionRef = useRef<number>(0);

  const stopAllAudio = () => {
    audioRefs.current.forEach((a) => {
      if (a) {
        a.pause();
        a.currentTime = 0;
      }
    });
  };

  const resetState = () => {
    playSessionRef.current += 1;
    stopAllAudio();
    setIsPlaying(false);
    setCurrentAudioIdx(0);
    setIsInitial(true);
  };

  // sources 변경 시 재생 중인 오디오 정지
  useEffect(() => {
    return () => {
      resetState();
    };
  }, [stableSources]);

  // unmount 시 정리
  useEffect(() => {
    return () => {
      playSessionRef.current += 1;
      stopAllAudio();
    };
  }, []);

  useEffect(() => {
    const sessionId = playSessionRef.current;

    if (!isPlaying) return;

    (async () => {
      if (currentAudioIdx === 0 && isInitial) {
        await sleep(timingBegin);
        if (sessionId !== playSessionRef.current) return;
        setIsInitial(false);
        return;
      }

      if (currentAudioIdx > 0) {
        await sleep(timingGap);
      }

      if (sessionId !== playSessionRef.current) return;

      const audio = audioRefs.current[currentAudioIdx];
      if (!audio) return;

      try {
        await audio.play();
      } catch {
        // play()가 pause()에 의해 중단된 경우 무시
      }
    })();
  }, [currentAudioIdx, isPlaying, isInitial, timingBegin, timingGap]);

  const handlePlayCompleted = async () => {
    const sessionId = playSessionRef.current;

    if (sources && currentAudioIdx < sources.length - 1) {
      setCurrentAudioIdx((prev) => prev + 1);
    } else {
      await sleep(timingEnd);
      if (sessionId !== playSessionRef.current) return;
      setIsPlaying(false);
      setCurrentAudioIdx(0);
      setIsInitial(true);
    }
  };

  const handlePause = () => {
    playSessionRef.current += 1;
    setIsPlaying(false);
    setCurrentAudioIdx(0);
    setIsInitial(true);
  };

  const handlePlayAndPauseClick = useClicker(
    async () => {
      if (!isPlaying) {
        if (isInitial) {
          setIsReplayable(true);
        }
        setIsPlaying(true);
      } else {
        playSessionRef.current += 1;
        stopAllAudio();
        setIsPlaying(false);
        setCurrentAudioIdx(0);
        setIsInitial(true);
      }
    },
    500,
    'throttle',
  );

  const handleReplayClick = useClicker(
    async () => {
      playSessionRef.current += 1;
      stopAllAudio();
      setCurrentAudioIdx(0);
      setIsInitial(true);
      setIsPlaying(true);
    },
    500,
    'throttle',
  );

  return {
    isPlaying,
    isReplayable,
    audioRefs,
    currentAudioIdx,
    handlePlayCompleted,
    handlePause,
    handlePlayAndPauseClick,
    handleReplayClick,
  };
};
